import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flag, X, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const REASONS = [
  { key: "offensive", labelKey: "report.reasonOffensive" },
  { key: "harmful", labelKey: "report.reasonHarmful" },
  { key: "inappropriate", labelKey: "report.reasonInappropriate" },
  { key: "incorrect", labelKey: "report.reasonIncorrect" },
  { key: "other", labelKey: "report.reasonOther" },
];

export function ReportButton({ decisionId, context }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        data-testid="report-response-btn"
        onClick={() => setOpen(true)}
        className="mx-auto mt-6 flex items-center gap-1.5 text-[13px] font-medium text-decide-mute transition-colors hover:text-decide-slate"
      >
        <Flag size={13} /> {t("report.trigger")}
      </button>
      <ReportSheet open={open} onClose={() => setOpen(false)} decisionId={decisionId} context={context} />
    </>
  );
}

function ReportSheet({ open, onClose, decisionId, context }) {
  const { t } = useLang();
  const [reason, setReason] = useState(null);
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason) { toast.error(t("report.selectReason")); return; }
    setBusy(true);
    try {
      await api.post("/reports", { reason, details, decision_id: decisionId || null, context: context || null });
      toast.success(t("report.success"));
      setReason(null); setDetails(""); onClose();
    } catch (e) {
      toast.error(t("report.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="fixed inset-0 z-50 bg-black/40" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => !busy && onClose()} />
          <motion.div
            data-testid="report-sheet"
            className="fixed bottom-0 inset-x-0 z-50 mx-auto max-w-lg bg-decide-canvas rounded-t-3xl border-t border-decide-line px-6 pt-3 pb-8"
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 2rem)" }}
          >
            <div className="mx-auto mb-5 h-1.5 w-10 rounded-full bg-decide-line" />
            <button data-testid="report-close" onClick={() => !busy && onClose()} className="absolute right-5 top-5 text-decide-mute hover:text-decide-ink" aria-label="Close"><X size={22} /></button>
            <h3 className="text-xl font-semibold tracking-tight text-decide-ink">{t("report.title")}</h3>
            <p className="mt-1.5 text-[14px] text-decide-slate leading-relaxed">{t("report.sub")}</p>

            <div className="mt-5 flex flex-wrap gap-2">
              {REASONS.map((r) => {
                const active = reason === r.key;
                return (
                  <button
                    key={r.key}
                    data-testid={`report-reason-${r.key}`}
                    onClick={() => setReason(r.key)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-medium transition-all ${active ? "bg-decide-forest text-white border border-decide-forest" : "bg-decide-surface text-decide-slate border border-decide-line hover:border-decide-ink hover:text-decide-ink"}`}
                  >
                    {active && <Check size={14} strokeWidth={3} />} {t(r.labelKey)}
                  </button>
                );
              })}
            </div>

            <textarea
              data-testid="report-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder={t("report.detailsPlaceholder")}
              rows={3}
              className="mt-4 w-full resize-none rounded-2xl border border-decide-line bg-decide-surface px-4 py-3 text-[15px] text-decide-ink placeholder:text-decide-mute focus:border-decide-forest focus:outline-none"
            />

            <button data-testid="report-submit" disabled={busy} onClick={submit} className="btn-accent mt-4 w-full disabled:opacity-50">
              {t("report.submit")}
            </button>
            <button data-testid="report-cancel" disabled={busy} onClick={onClose} className="btn-ghost mt-2 w-full">{t("report.cancel")}</button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
